// @ts-nocheck
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const RULE_LABELS: Record<string, string> = {
  record: "Record",
  winning_percentage: "Winning percentage",
  head_to_head: "Head to head",
  head_to_head_two_teams_only: "Head to head (two teams only)",
  head_to_head_point_differential: "Head-to-head point differential",
  point_differential: "Point differential",
  points_scored: "Points scored",
  points_allowed: "Fewest points allowed",
  standing_points: "Standings points (win/tie/loss values)",
  pool_place: "Pool place",
};

/** Legacy key kept working: points_for was the old name for points_scored. */
const ALIASES: Record<string, string> = { points_for: "points_scored" };

const POOL_KEYS = [
  "record",
  "winning_percentage",
  "head_to_head",
  "head_to_head_two_teams_only",
  "head_to_head_point_differential",
  "point_differential",
  "points_scored",
  "points_allowed",
  "standing_points",
];

const DIVISION_KEYS = ["pool_place", ...POOL_KEYS];

const DEFAULT_POOL = [
  "head_to_head",
  "point_differential",
  "points_allowed",
  "points_scored",
];

const DEFAULT_DIVISION = [
  "pool_place",
  "record",
  "head_to_head",
  "point_differential",
  "points_scored",
];

interface Props {
  eventId: string | undefined;
}

const normalize = (raw: any, allowed: string[], fallback: string[]) => {
  const saved = Array.isArray(raw)
    ? raw.map((k: string) => ALIASES[k] ?? k).filter((k: string) => allowed.includes(k))
    : [];
  const base = saved.length ? saved : fallback;
  return [...base, ...allowed.filter((k) => !base.includes(k))];
};

function Chain({
  title,
  hint,
  order,
  setOrder,
  active,
  setActive,
}: {
  title: string;
  hint: string;
  order: string[];
  setOrder: (fn: (prev: string[]) => string[]) => void;
  active: number;
  setActive: (n: number) => void;
}) {
  const move = (i: number, dir: -1 | 1) => {
    setOrder((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-bold">{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {order.map((key, i) => {
          const inUse = i < active;
          return (
            <div
              key={key}
              className={`flex items-center gap-3 px-4 py-2.5 ${inUse ? "" : "opacity-50"}`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === order.length - 1}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                {inUse ? i + 1 : "–"}
              </span>
              <span className="text-sm font-medium text-foreground flex-1">
                {RULE_LABELS[key] || key}
              </span>
              <button
                type="button"
                className="text-xs text-primary underline"
                onClick={() => setActive(inUse ? i : i + 1)}
              >
                {inUse ? "Not used" : "Use"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StandingsSection({ eventId }: Props) {
  const [poolOrder, setPoolOrder] = useState<string[]>(normalize(null, POOL_KEYS, DEFAULT_POOL));
  const [divOrder, setDivOrder] = useState<string[]>(normalize(null, DIVISION_KEYS, DEFAULT_DIVISION));
  const [poolActive, setPoolActive] = useState(DEFAULT_POOL.length);
  const [divActive, setDivActive] = useState(DEFAULT_DIVISION.length);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasRow, setHasRow] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("event_format_config")
        .select("tiebreaker_order, tiebreaker_order_division")
        .eq("event_id", eventId)
        .maybeSingle();
      setLoading(false);
      if (error) {
        console.error("load tiebreaker chains error", error);
        return;
      }
      const savedPool = Array.isArray(data?.tiebreaker_order) ? data.tiebreaker_order : null;
      const savedDiv = Array.isArray(data?.tiebreaker_order_division) ? data.tiebreaker_order_division : null;
      const p = normalize(savedPool, POOL_KEYS, DEFAULT_POOL);
      const d = normalize(savedDiv, DIVISION_KEYS, DEFAULT_DIVISION);
      setPoolOrder(p);
      setDivOrder(d);
      setPoolActive((savedPool?.length ? savedPool : DEFAULT_POOL).length);
      setDivActive((savedDiv?.length ? savedDiv : DEFAULT_DIVISION).length);
      setHasRow(!!data);
    };
    load();
  }, [eventId]);

  const save = async () => {
    if (!eventId) {
      toast.error("Save the event first.");
      return;
    }
    setSaving(true);
    const { error } = await (supabase as any)
      .from("event_format_config")
      .upsert(
        {
          event_id: eventId,
          tiebreaker_order: poolOrder.slice(0, Math.max(1, poolActive)),
          tiebreaker_order_division: divOrder.slice(0, Math.max(1, divActive)),
        },
        { onConflict: "event_id" }
      );
    setSaving(false);
    if (error) {
      toast.error(error.message || "Failed to save tie-breakers.");
    } else {
      setHasRow(true);
      toast.success("Tie-breakers saved.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight">STANDINGS &amp; TIE-BREAKERS</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Two chains, because they answer different questions: how teams are ranked inside a pool, and how
          they are ranked across a whole division. Tied teams that no rule separates are reported, never
          ordered by team name. A division can override either chain in its own settings.
        </p>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <div className="space-y-6">
          <Chain
            title="Within a pool"
            hint="Applied in order until the tie is broken. Head to head with three or more teams only applies when they have all played each other."
            order={poolOrder}
            setOrder={setPoolOrder}
            active={poolActive}
            setActive={setPoolActive}
          />
          <Chain
            title="Across the division"
            hint="Used for final division places when a division has several pools."
            order={divOrder}
            setOrder={setDivOrder}
            active={divActive}
            setActive={setDivActive}
          />

          {!hasRow && (
            <p className="text-xs text-amber-600">
              Nothing saved yet. The defaults above will be used once you save.
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="button" onClick={save} disabled={saving || !eventId}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save tie-breakers
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
